import { forwardRef } from "react";
import { MapPin, Mountain, Building2, Palette } from "lucide-react";
import StageCard from "./StageCard";
import type { StageStatus } from "./StageCard";

interface StageProgressProps {
  stages: Array<{ id: number; status: StageStatus }>;
  address: string;
  paletteName: string;
}

// 4 user-visible stages (backend stage 5 is instant, skip it)
const STAGE_DEFS = [
  {
    icon: MapPin,
    titleFn: () => "Mapping your location",
    descFn: (addr: string) => `Locating ${addr}...`,
  },
  {
    icon: Mountain,
    titleFn: () => "Scanning terrain",
    descFn: () => "Reading elevation data for your area",
  },
  {
    icon: Building2,
    titleFn: () => "Building 3D structures",
    descFn: () => "Creating buildings, roads, and landmarks",
  },
  {
    icon: Palette,
    titleFn: () => "Applying your style",
    descFn: (_: string, pal: string) => `Coloring with ${pal}`,
  },
];

const VISIBLE_STAGE_COUNT = 4;

const StageProgress = forwardRef<HTMLDivElement, StageProgressProps>(
  ({ stages, address, paletteName }, ref) => {
    const visibleStages = stages.slice(0, VISIBLE_STAGE_COUNT);
    const completedCount = visibleStages.filter((s) => s.status === "completed").length;
    const hasActive = visibleStages.some((s) => s.status === "active");
    const allPending = visibleStages.every((s) => s.status === "pending");
    const allDone = visibleStages.every((s) => s.status === "completed");

    // Between steps: some completed but no active step yet
    const inGap = completedCount > 0 && !hasActive && !allDone;

    const stepSize = 100 / VISIBLE_STAGE_COUNT;
    // Cap at 95% when all 4 visible stages complete. In Flow A,
    // StageProgress remains visible during the crossfade period
    // (previewState === 'result' but isViewerReady === false), then
    // fades out once ThreeDViewer is ready. The bar stays at 95%
    // during this brief crossfade — no need to reach 100%.
    const MAX_STAGE_PROGRESS = 95;
    const progress = Math.min(
      completedCount * stepSize + (hasActive || inGap ? stepSize / 2 : allPending ? 2 : 0),
      allDone ? MAX_STAGE_PROGRESS : 100
    );

    return (
      <div ref={ref} className="flex-[7] p-8 flex flex-col justify-center" tabIndex={-1}>
        <h3 className="text-lg font-semibold text-slate-700 mb-2">
          Building your 3D map...
        </h3>

        {/* Progress bar */}
        <div className="mb-6">
          <div
            className="h-1.5 bg-gray-100 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            aria-label="3D map generation progress"
          >
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out ${
                inGap ? "animate-progressPulse" : ""
              }`}
              style={{
                width: `${progress}%`,
                backgroundColor: "var(--sage-400)",
              }}
            />
          </div>
          <div className="mt-1.5 text-xs text-slate-400" aria-live="polite">
            {allPending
              ? "Getting ready — first request may take a moment..."
              : allDone
                ? "Almost there..."
                : `Step ${completedCount + (hasActive || inGap ? 1 : 0)} of ${VISIBLE_STAGE_COUNT}`}
          </div>
        </div>

        <div className="space-y-4" role="list" aria-label="3D map generation progress">
          {visibleStages.map((stage, i) => {
            const def = STAGE_DEFS[i];
            const effectiveStatus: StageStatus =
              inGap && stage.status === "pending" && i === completedCount
                ? "active"
                : stage.status;

            return (
              <StageCard
                key={stage.id}
                icon={def.icon}
                title={def.titleFn(address, paletteName)}
                description={def.descFn(address, paletteName)}
                status={effectiveStatus}
                index={i}
              />
            );
          })}
        </div>
      </div>
    );
  }
);

StageProgress.displayName = "StageProgress";
export default StageProgress;
